import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { TableHeader, Spinner, EmployeeLimitModal } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { Col, Card, ListGroup, Button, Row } from "react-bootstrap";
import { useReactiveVar } from "@apollo/client";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import SidebarService from "@me-team/common/src/services/SidebarService/SidebarService";
import { useGetCurrentBranchIdQuery } from "@me-team/host/main/branches/graphql/branches.hooks";
import { useGetCompanyModulesQuery } from "@me-team/host/main/company/graphql/company.hooks";
import { ModuleProxy } from "@me-team/host/main/graphql/types";
import ConnectModal from "../Joyride/modals/ConnectModal";
import { useToast } from "../../hooks/useToast";
import ToastComponent from "../ui-components/ToastComponent/ToastComponent";
import { currentBranchIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import AddPreTrainingModuleModal from "./AddPreTrainingModuleModal";
import { AllModulesTagName } from "../../utils/enums";
import { StartWarehouseTutorialButton, StartWarehouseTutorialModal } from "../index";
import { useTutorialAccess } from "../Joyride/useTutorialAccess";

const MainMenuBeautySalon = () => {
    const { t, i18n } = useTranslation();
    const INDONESIAN_LANGUAGE = "id";
    const isIndonesianLanguage = i18n.language === INDONESIAN_LANGUAGE;
    const location = useLocation();
    const { branchId } = useParams();
    const [openConnectModal, setOpenConnectModal] = useState<boolean>(false);
    const shouldOpenConnectModalAfterCreation = Boolean(location?.state?.creat);
    const hasOpenedConnectModalAfterCreation = useRef<boolean>(false);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchId = useReactiveVar(currentBranchIdVar);
    const { showToast, toastText, setShowToast } = useToast();
    const [isOpenAddPreTrainingModuleModal, setOpenAddPreTrainingModuleModal] = useState<boolean>(false);
    const [showStartWarehouseTutorialModal, setShowStartWarehouseTutorialModal] = useState<boolean>(false);

    const handleCloseStartWarehouseTutorialModal = () => setShowStartWarehouseTutorialModal(false);

    const toggleAddPreTrainingModuleModal = () => {
        setOpenAddPreTrainingModuleModal((prevState) => !prevState);
    };
    const toggleOpenConnectModal = () => {
        setOpenConnectModal((prevState) => !prevState);
    };
    const [showEmployeeLimitModal, setShowEmployeeLimitModal] = useState(false);

    const toggleEmployeeLimitModal = () => {
        setShowEmployeeLimitModal((prevState) => !prevState);
    };

    const { data: currentBranchData, loading } = useGetCurrentBranchIdQuery({
        skip: !currentCompanyId || !currentBranchId,
        fetchPolicy: "network-only",
    });

    const { data: modulesData, refetch: moduleListRefetch } = useGetCompanyModulesQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
    });
    const modulesList: ModuleProxy[] = modulesData?.user?.company?.modules || [];
    const sidebarService = new SidebarService(modulesList);
    const { canStartWarehouseTutorial, canStartOnlineBookingTutorial } = useTutorialAccess();
    const currentBranchName = currentBranchData?.user?.currentBranch?.name;
    useDocumentTitle(currentBranchName);

    const isOnlineBookingModuleActive = modulesList.some(
        (module) => module.name === AllModulesTagName.BOOKING && module.active
    );

    useEffect(() => {
        if (
            !shouldOpenConnectModalAfterCreation ||
            hasOpenedConnectModalAfterCreation.current ||
            !modulesData ||
            !isOnlineBookingModuleActive ||
            !canStartOnlineBookingTutorial
        ) {
            return;
        }

        setOpenConnectModal(true);
        hasOpenedConnectModalAfterCreation.current = true;
    }, [shouldOpenConnectModalAfterCreation, modulesData, isOnlineBookingModuleActive, canStartOnlineBookingTutorial]);

    const handleStartTutorial = () => {
        if (!isOnlineBookingModuleActive) {
            setOpenAddPreTrainingModuleModal(true);
            return;
        }
        setOpenConnectModal(true);
    };

    const sections = sidebarService.getSections(t, branchId, currentCompanyId);

    const sortedSections = sections.sort((a, b) => {
        if (a.active && !b.active) return -1;
        if (!a.active && b.active) return 1;
        return 0;
    });

    if (loading) return <Spinner />;

    return (
        <>
            <TableHeader
                title={currentBranchName}
                paths={[
                    { path: `/branches/${branchId}`, label: t("branches") },
                    { path: `/branches/main/branch/${branchId}`, label: currentBranchName },
                ]}
                buttons={
                    isIndonesianLanguage ? (
                        <Row
                            className={`d-flex flex-wrap justify-content-end gap-3 ${canStartWarehouseTutorial ? "" : "gap-xxl-0 mt-3 mt-lg-0"}`}
                        >
                            {canStartWarehouseTutorial && (
                                <Col lg={12} xl={11} xxl={9} className="mt-3 mt-lg-0">
                                    <StartWarehouseTutorialButton
                                        handleClick={() => setShowStartWarehouseTutorialModal(true)}
                                        classBtn="text-truncate w-100 px-2"
                                    />
                                </Col>
                            )}
                            {canStartOnlineBookingTutorial && (
                                <Col lg={12} xl={11} xxl={9}>
                                    <Button
                                        variant={"outline-primary"}
                                        className={`fw-normal connect-online-booking w-100 px-1`}
                                        onClick={handleStartTutorial}
                                    >
                                        {t("How to connect Online Booking?")}
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    ) : (
                        <Row
                            className={`d-flex flex-wrap justify-content-end gap-3 ${canStartWarehouseTutorial ? "flex-xxl-nowrap gap-xxl-0" : "gap-xxl-0 mt-3 mt-lg-0"}`}
                        >
                            {canStartWarehouseTutorial && (
                                <Col lg={9} xl={8} xxl={6} className="pe-xxl-2 mt-3 mt-lg-0">
                                    <StartWarehouseTutorialButton
                                        handleClick={() => setShowStartWarehouseTutorialModal(true)}
                                        classBtn="text-truncate w-100 px-2"
                                    />
                                </Col>
                            )}

                            {canStartOnlineBookingTutorial && (
                                <Col lg={9} xl={8} xxl={6} className={canStartWarehouseTutorial ? "ps-xxl-2" : ""}>
                                    <Button
                                        variant={"outline-primary"}
                                        className={`fw-normal connect-online-booking w-100 ${canStartWarehouseTutorial ? "text-truncate px-2" : "px-1"}`}
                                        onClick={handleStartTutorial}
                                    >
                                        {t("How to connect Online Booking?")}
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    )
                }
                colSize={6}
            />

            <div className="row justify-content mt-5">
                {sortedSections.map((section, index) => (
                    <Col key={index} lg={6} xxl={4} className="mb-4">
                        <Card
                            className={`d-flex flex-column h-100 border border-secondary rounded-3 p-4 ${!section.active ? "border-opacity-50" : ""}`}
                        >
                            <Card.Header className="mb-4">
                                <h5 className={`fs-4 mb-0 ${!section.active ? "opacity-50" : ""}`}>{section.title}</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-flex justify-content-center mb-4">
                                    <img
                                        className={!section.active ? "opacity-50" : ""}
                                        src={section.image}
                                        alt={section.title}
                                    />
                                </div>
                                <ListGroup as="ul" className="list-grid list-unstyled mb-4">
                                    {section.items.filter(Boolean).map((item, index) => {
                                        return (
                                            <ListGroup.Item key={index} as="li" className="py-2">
                                                <Link
                                                    to={item.to}
                                                    className={`text-decoration-none ${!item.active ? "pe-none text-secondary" : "text-violet-hover"} ${!section.active ? "pe-none opacity-50" : ""}`}
                                                >
                                                    {item.label}
                                                </Link>
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            </Card.Body>
                            {!section.isHidden && (
                                <Card.Footer className="bg-white border-top-0 mt-auto">
                                    <Link
                                        to={
                                            section.active
                                                ? section.navigateTo
                                                : section.disabledNavigateTo ||
                                                  `/payment/company/${currentCompanyId}/modules/branch/${branchId}`
                                        }
                                        className="btn btn-outline-primary btn-menu-card w-100 fw-normal rounded-1 border-primary text-primary"
                                    >
                                        {section.active ? section.linkLabel : t("View")}
                                    </Link>
                                </Card.Footer>
                            )}
                        </Card>
                    </Col>
                ))}
            </div>
            <EmployeeLimitModal
                isCheckLimitBeforeTutorial
                showEmployeeLimitModal={showEmployeeLimitModal}
                handleCloseEmployeeLimitModal={toggleEmployeeLimitModal}
            />
            <ConnectModal
                show={openConnectModal}
                setOpenConnectModal={setOpenConnectModal}
                toggleEmployeeLimitModal={toggleEmployeeLimitModal}
            />
            <AddPreTrainingModuleModal
                show={isOpenAddPreTrainingModuleModal}
                onClose={toggleAddPreTrainingModuleModal}
                companyId={Number(currentCompanyId)}
                toggleOpenConnectModal={toggleOpenConnectModal}
                moduleListRefetch={moduleListRefetch}
            />
            {showStartWarehouseTutorialModal && (
                <StartWarehouseTutorialModal
                    show={showStartWarehouseTutorialModal}
                    handleClose={handleCloseStartWarehouseTutorialModal}
                />
            )}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default MainMenuBeautySalon;
